import { useMemo } from 'react';
import { NoteCard } from './NoteCard';
import type { TenantNote } from '../../data/types';

interface NoteListProps {
  notes: TenantNote[];
  canModifyNote: (note: TenantNote) => boolean;
  onUpdated: (note: TenantNote) => void;
  onDeleted: (noteId: string) => void;
}

function monthKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
}

/**
 * Renders a customer's notebook grouped by calendar month (newest month first),
 * with pinned notes floated to the top within each month.
 */
export function NoteList({ notes, canModifyNote, onUpdated, onDeleted }: NoteListProps) {
  const groups = useMemo(() => {
    const map = new Map<string, TenantNote[]>();
    for (const n of notes) {
      const k = monthKey(n.createdAt);
      const bucket = map.get(k);
      if (bucket) bucket.push(n);
      else map.set(k, [n]);
    }
    return Array.from(map.keys())
      .sort()
      .reverse()
      .map((k) => {
        const arr = map.get(k)!.slice().sort((a, b) => {
          if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });
        return { key: k, label: monthLabel(arr[0].createdAt), notes: arr };
      });
  }, [notes]);

  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <section key={group.key}>
          <div className="sticky top-0 z-10 -mx-1 mb-2 flex items-center gap-2 bg-bg-primary/80 backdrop-blur px-1 py-1">
            <h3 className="text-sm font-semibold text-text-secondary">{group.label}</h3>
            <span className="text-xs text-text-muted">
              {group.notes.length} {group.notes.length === 1 ? 'note' : 'notes'}
            </span>
          </div>
          <div className="space-y-3">
            {group.notes.map((note) => (
              <NoteCard
                key={note.noteId}
                note={note}
                canModify={canModifyNote(note)}
                onUpdated={onUpdated}
                onDeleted={onDeleted}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
